import { useEffect, useState } from "react";
import { ArrowUpRight, FileText, Home, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import ReservationAlertsPanel from "@/components/ReservationAlertsPanel";

const formatter = new Intl.NumberFormat("es-PY");

export default function DashboardPage() {
  const { formatAmount } = useCurrency();
  const [summary, setSummary] = useState(null);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/dashboard/summary"), api.get("/quotes")]).then(
      ([summaryResponse, quoteResponse]) => {
        setSummary(summaryResponse.data);
        setQuotes(quoteResponse.data.slice(0, 5));
      },
    );
  }, []);

  if (!summary) {
    return <div className="page-loading" data-testid="dashboard-loading">Cargando panel...</div>;
  }

  const stats = [
    { label: "Lotes registrados", value: summary.lotes, icon: Home, tone: "green" },
    { label: "Disponibles", value: summary.disponibles, icon: Sparkles, tone: "mint" },
    { label: "Reservados", value: summary.reservados, icon: FileText, tone: "amber" },
    { label: "Cotizaciones", value: summary.cotizaciones, icon: FileText, tone: "stone" },
  ];

  return (
    <section className="page dashboard-page" data-testid="dashboard-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">VISTA GENERAL</span>
          <h1>Buen día, el terreno está listo.</h1>
          <p>Una lectura rápida del inventario y la actividad comercial de hoy.</p>
        </div>
        <Link className="primary-button" data-testid="dashboard-create-quote-link" to="/cotizador">
          Nueva cotización <ArrowUpRight size={18} />
        </Link>
      </header>

      <div className="stat-grid" data-testid="dashboard-stats-grid">
        {stats.map(({ label, value, icon: Icon, tone }) => (
          <article className={`stat-card ${tone}`} data-testid={`dashboard-stat-${tone}`} key={label}>
            <div className="stat-icon"><Icon size={19} /></div>
            <span>{label}</span>
            <strong>{formatter.format(value)}</strong>
          </article>
        ))}
      </div>

      <ReservationAlertsPanel />

      <div className="dashboard-grid">
        <section className="activity-panel" data-testid="recent-quotes-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">ACTIVIDAD</span>
              <h2>Últimas cotizaciones</h2>
            </div>
            <span data-testid="active-promotions-count">
              {summary.promociones_activas} promociones activas
            </span>
          </div>
          {quotes.length ? (
            <div className="quote-list">
              {quotes.map((quote) => (
                <div className="quote-row" data-testid={`dashboard-quote-${quote.id}`} key={quote.id}>
                  <span className="quote-badge">{quote.lot.manzano} · {quote.lot.lote}</span>
                  <span><strong>{quote.client.nombre}</strong><small>{quote.vendedor}</small></span>
                  <span>{quote.meses} meses</span>
                  <span className="quote-value">{formatAmount(quote.valor_cuota)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-inline" data-testid="empty-quotes-message">
              Aún no hay cotizaciones registradas.
            </div>
          )}
        </section>
        <aside className="inventory-panel" data-testid="inventory-overview-panel">
          <span className="eyebrow">INVENTARIO</span>
          <h2>Estado de lotes</h2>
          <div className="inventory-meter">
            <span className="available" style={{ width: `${(summary.disponibles / summary.lotes) * 100}%` }} />
            <span className="reserved" style={{ width: `${(summary.reservados / summary.lotes) * 100}%` }} />
            <span className="sold" style={{ width: `${(summary.vendidos / summary.lotes) * 100}%` }} />
          </div>
          <dl>
            <div><dt>Disponibles</dt><dd>{summary.disponibles}</dd></div>
            <div><dt>Reservados</dt><dd>{summary.reservados}</dd></div>
            <div><dt>Vendidos</dt><dd>{summary.vendidos}</dd></div>
          </dl>
        </aside>
      </div>
    </section>
  );
}
